import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faBuilding,
  faFileAlt,
  faShieldAlt,
} from '@fortawesome/free-solid-svg-icons';
import { InfortunioCompletedScreenStyles } from './completedScreens.styles';
import {
  CompletedCard,
  CompletedCardHeader,
  CompletedCardIcon,
  CompletedCardTitle,
  CompletedFieldGrid,
  CompletedField,
  CompletedSectionTitle,
} from './CompletedV2.styles';
import ContractDocuments from '../../common/components/elements/ContractDocuments';
import ErrorComponent from '../../common/components/ErrorComponent';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import {
  getInfortunioCompletedData, infortunioCompletedDataReset,
} from '../../features/purchases/purchasesActions';
import { apiDownloadDocument, apiDownloadOptionalDocument } from '../../services/axios-client/axeCommons';
import quotationsAxe from '../../services/axios-client/quotationsAxeClient';
import CompletedQuoteScreenAutoAfterSale from '../../common/components/elements/CompletedQuoteScreenAutoAfterSale';
import CompletedScreenInfoInfortunio from '../../common/components/elements/CompletedScreenInfoInfortunio';
import CompletedScreenInfortunioDocuments from '../../common/components/elements/CompletedScreenInfortunioDocuments';

function InfortunioCompletedScreen() {
  const [residenceInfo, setResidenceInfo] = useState(null);
  const { quoteId } = useParams();
  const { document_types } = useSelector(
    (store) => store.afterSale,
  );
  const {
    loading, quote, error, risk_quotations,
  } = useSelector(
    (store) => store.purchases.infortunio_completed,
  );

  const collectMunicipalities = () => {
    try {
      quotationsAxe.get('/form-data/communes').then((res) => {
        const commune = res.data.data.find(
          (commune) => commune.cadastral_code
            == quote.insurance_request.customer.residence_commune_code,
        );
        setResidenceInfo(commune);
      });
    } catch (err) {
      // Do nothing
    }
  };

  const downloadDocument = async (documentId) => {
    const response = await apiDownloadDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  const downloadOptionalDocument = async (documentId) => {
    const response = await apiDownloadOptionalDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  const collectQuoteData = () => {
    getInfortunioCompletedData(quoteId);
  };

  useEffect(() => {
    collectQuoteData();

    return () => {
      infortunioCompletedDataReset();
    };
  }, [quoteId]);

  useEffect(() => {
    if (quote && quote.status === 7) {
      collectMunicipalities();
    }
  }, [quote]);

  if (loading) {
    return <PageLoading />;
  }

  if (loading === false && error !== null) {
    return <ErrorComponent error={error} />;
  }

  const documentsRequests = [
    ...quote.insurance_request.document_requests.filter(
      (dr) => dr.document !== null,
    ),
    ...quote.document_requests.filter((dr) => dr.document !== null),
  ];

  return (
    <InfortunioCompletedScreenStyles>
      {/* Contract Documents Section */}
      <div className="section-container">
        <ContractDocuments
          downloadDocument={downloadDocument}
          insuranceRequest={quote.insurance_request}
          quote={quote}
          collectQuoteData={collectQuoteData}
          v2
        />
      </div>

      {/* Customer Information Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faFileAlt} className="section-icon" />
          <h2>Informazioni Polizza</h2>
        </CompletedSectionTitle>

        <div className="cards-row">
          {/* Customer Information Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-one">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon
                    icon={quote.insurance_request.customer.gender === 'G' ? faBuilding : faUser}
                  />
                </CompletedCardIcon>
                <CompletedCardTitle>Anagrafica</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField className="full">
                  <dt>
                    {quote.insurance_request.customer.gender === 'G' ? 'Compagnia' : 'Nome e Cognome'}
                  </dt>
                  <dd>
                    {quote.insurance_request.customer.gender === 'G'
                      ? quote.insurance_request.customer.business_name
                      : `${quote.insurance_request.customer.name} ${quote.insurance_request.customer.surname}`}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Email</dt>
                  <dd>{quote.insurance_request.customer.email}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Telefono</dt>
                  <dd>{quote.insurance_request.customer.phone}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Indirizzo</dt>
                  <dd>
                    {quote.insurance_request.customer.address}
                    ,
                    {' '}
                    {quote.insurance_request.customer.house_number}
                    ,
                    {' '}
                    {quote.insurance_request.customer.postal_code}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Residenza</dt>
                  <dd>{residenceInfo !== null && residenceInfo.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Città</dt>
                  <dd>
                    {residenceInfo !== null && residenceInfo.communal_territory.name}
                  </dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Prodotto</dt>
                  <dd>{quote.product.name}</dd>
                </CompletedField>

                {quote.insurance_request.has_retroactivity_illimitata !== null && (
                  <CompletedField className="full">
                    <dt>Retroattività Illimitata</dt>
                    <dd>
                      {quote.insurance_request.has_retroactivity_illimitata === '1' ? 'Si' : 'No'}
                    </dd>
                  </CompletedField>
                )}

                {quote.insurance_request.customer.family_household_members !== null && (
                  <CompletedField className="full">
                    <dt>Componenti Nucleo Famigliare</dt>
                    <dd>
                      {quote.insurance_request.customer.family_household_members}
                    </dd>
                  </CompletedField>
                )}
              </CompletedFieldGrid>
            </CompletedCard>
          </div>
        </div>
      </div>

      {/* Quote Details & Documents Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
          <h2>Dettagli Preventivo e Documenti</h2>
        </CompletedSectionTitle>

        <div className="row">
          <CompletedScreenInfoInfortunio
            rc_quotation={quote}
            risk_quotations={risk_quotations}
          />

          <CompletedScreenInfortunioDocuments
            downloadDocument={downloadDocument}
            insuranceRequest={quote.insurance_request}
            quote={quote}
            collectQuoteData={collectQuoteData}
            documentsRequests={documentsRequests}
            downloadOptionalDocument={downloadOptionalDocument}
            rc_quotation={quote}
          />
        </div>
      </div>

      {/* After Sale Section */}
      <CompletedQuoteScreenAutoAfterSale quote={quote} document_types={document_types} v2 />
    </InfortunioCompletedScreenStyles>
  );
}

export default InfortunioCompletedScreen;
